import AppContext from 'App/AppContext';
import { isCopilotPanelAvailable } from 'components/Copilot/copilotPanelAvailability';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { CopilotTaskChecklistSuggestions } from 'models/copilot/copilotTaskChecklistSuggestions';
import Task from 'models/tasks/task';
import { TaskCheckListItem } from 'models/tasks/taskHelperClasses';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotTaskChecklistSuggestions } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { htmlToText } from 'utils/html';
import { WizardStep } from './NewTaskWizard';
import { newTaskWizardStepsTemplate } from './newTaskWizardAIActionTemplates';

/** The steps one AI action added, so that Undo takes back only these. */
interface IWizardAIChange {
  addedSteps: { id: string; description: string }[];
}

/** What the AI changed. Undo takes back only that. */
export interface IWizardAIBar {
  step: WizardStep;
  lines: string[];
  change: IWizardAIChange;
}

interface IUseNewTaskWizardAIProps {
  isOpen: boolean;
  task: Task;
  canUpdate: boolean;
  onUpdateTask: (task: Task) => void;
}

//true when the user changed a step the AI added, so Undo would throw away the edit
const isChangeEdited = (task: Task, change: IWizardAIChange): boolean => {
  return change.addedSteps.some((step) => {
    const item = task.checkList.items.find((candidate) => candidate.id === step.id);

    return !!item && (item.description ?? '').trim() !== step.description;
  });
};

//takes back only what the action added, on a copy of the task as it is now
const revertChange = (task: Task, change: IWizardAIChange): Task => {
  const copy = task.clone();

  copy.checkList.items = copy.checkList.items.filter((item) => !change.addedSteps.some((step) => step.id === item.id));

  return copy;
};

const getStepDescriptions = (task: Task): string[] => {
  return task.checkList.items.map((item) => item.description?.trim()).filter((text): text is string => !!text);
};

//appends the steps that the checklist does not have yet (compared case-insensitively); returns
//the added items
const appendSteps = (copy: Task, steps: string[]): TaskCheckListItem[] => {
  const known = new Set(getStepDescriptions(copy).map((text) => text.toLowerCase()));
  const added: TaskCheckListItem[] = [];

  for (const step of steps) {
    const text = step.trim();

    if (text && !known.has(text.toLowerCase())) {
      known.add(text.toLowerCase());
      const item = new TaskCheckListItem();
      item.description = text;
      added.push(item);
    }
  }

  copy.checkList.items = [...copy.checkList.items, ...added];

  return added;
};

// The AI action of the new-task wizard: suggests checklist steps. It asks the server for a proposal
// and applies it to the task in the wizard only (never saves); the result bar offers Undo.
export const useNewTaskWizardAI = (props: IUseNewTaskWizardAIProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const [running, setRunning] = useState<boolean>(false);
  const [bar, setBar] = useState<IWizardAIBar | undefined>(undefined);

  //the answer arrives later than the click: it is applied to the task as it is by then, and dropped
  //when the wizard was reopened in the meantime
  const taskRef = useRef<Task>(props.task);
  taskRef.current = props.task;
  const sessionRef = useRef<number>(0);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const inFlightRef = useRef<boolean>(false);

  useEffect(() => {
    sessionRef.current += 1;
    abortRef.current?.abort();
    setRunning(false);
    setBar(undefined);
  }, [props.isOpen]);

  //a reply that arrives after the wizard was closed is ignored
  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      abortRef.current?.abort();
    };
  }, []);

  //the result bar goes away once the user edits a step the AI added, so Undo never throws the edit away
  useEffect(() => {
    if (bar && isChangeEdited(props.task, bar.change)) setBar(undefined);
  }, [props.task, bar]);

  const notify = (message: string, isError: boolean) => {
    appContext.showNotification(message, isError, false, true);
  };

  const applySteps = (result: CopilotTaskChecklistSuggestions) => {
    const copy = taskRef.current.clone();
    const addedItems = appendSteps(copy, result.steps);

    if (addedItems.length === 0) {
      notify(result.message ?? t('task:NewTaskWizard.AI.NothingToApply'), false);

      return;
    }

    props.onUpdateTask(copy);
    setBar({
      step: 1,
      lines: [
        addedItems.length === 1
          ? t('task:NewTaskWizard.AI.StepsResult_one', { count: 1 })
          : t('task:NewTaskWizard.AI.StepsResult_other', { count: addedItems.length }),
      ],
      change: { addedSteps: addedItems.map((item) => ({ id: item.id, description: item.description?.trim() ?? '' })) },
    });
  };

  const run = async () => {
    //the ref is current at once; the state is stale on a fast double click
    if (running || inFlightRef.current) return;

    inFlightRef.current = true;
    const session = sessionRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    setBar(undefined);

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const task = taskRef.current;
      const result = await apiCopilotTaskChecklistSuggestions(
        task.name ?? '',
        htmlToText(task.description, false).trim(),
        getStepDescriptions(task),
        null,
        accessToken,
        controller.signal,
      );

      if (session !== sessionRef.current) return;

      if (!result) {
        notify(t('task:NewTaskWizard.AI.NothingToApply'), false);

        return;
      }

      applySteps(result);
    } catch (err) {
      //a request aborted on close is not an error
      if (session === sessionRef.current && !controller.signal.aborted) {
        const message = AppError.getControllerExceptionMsg(err, t);
        const appError = err as AppError;

        //a 403 without a body says nothing the user can act on
        if (!message && appError.code === '403' && !appError.debug) {
          notify(t('task:NewTaskWizard.AI.NotAvailable'), true);
        } else {
          notify(message ?? appError.message, true);
        }
      }
    } finally {
      inFlightRef.current = false;
      if (session === sessionRef.current) setRunning(false);
    }
  };

  const undo = () => {
    if (!bar) return;

    props.onUpdateTask(revertChange(taskRef.current, bar.change));
    setBar(undefined);
  };

  const isAvailable = isCopilotPanelAvailable(appContext) && hasUserFeatureGenericManager(appContext);
  const hasName = !!props.task.name?.trim();

  const getBindings = (): AIActionBinding[] => {
    if (!isAvailable) return [];

    const blocked = !hasName;

    return [
      createAIActionBinding(
        newTaskWizardStepsTemplate,
        { run: () => void run() },
        {
          disabled: running || !props.canUpdate || blocked,
          title: blocked ? t('task:NewTaskWizard.NameFirst') : undefined,
        },
      ),
    ];
  };

  return {
    getBindings,
    isRunning: running,
    bar,
    undo,
    dismissBar: () => setBar(undefined),
  };
};
